import { Component } from '@theme/component';
import { onDocumentLoaded, changeMetaThemeColor } from '@theme/utilities';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 * @property {HTMLElement} headerRowTop - The header top row element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu', 'headerRowTop'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * The currently tracked scroll direction.
   * @type {'up' | 'down' | 'none'}
   */
  #trackedDirection = 'none';

  /**
   * Accumulated travel distance for the current scroll direction.
   * @type {number}
   */
  #directionTravel = 0;

  /**
   * Minimum scroll delta to prevent sticky header jitter on small bounce movements.
   * @constant {number}
   */
  #scrollThreshold = 6;

  /**
   * Minimum directional travel before toggling sticky visibility.
   * @constant {number}
   */
  #directionSwitchThreshold = 18;

  /**
   * Keep header visible while close to the top of the page.
   * @constant {number}
   */
  #topVisibilityThreshold = 24;

  /**
   * Pending animation frame id for scroll state updates.
   * @type {number}
   */
  #scrollRaf = 0;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessaary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }
  });

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    const config = {
      threshold: alwaysSticky ? 1 : 0,
    };

    this.#intersectionObserver = new IntersectionObserver(([entry]) => {
      if (!entry) return;

      const { isIntersecting } = entry;

      if (alwaysSticky) {
        this.dataset.stickyState = isIntersecting ? 'inactive' : 'active';
        changeMetaThemeColor(this.refs.headerRowTop);
      }
    }, config);

    this.#intersectionObserver.observe(this);
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {OverflowMinimumEvent} event
   */
  #handleOverflowMinimum = (event) => {
    this.#updateMenuVisibility(event.detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.refs.headerDrawerContainer.classList.remove('desktop:hidden');
      this.#menuDrawerHiddenWidth = window.innerWidth;
      this.refs.headerMenu.classList.add('hidden');
    } else {
      this.refs.headerDrawerContainer.classList.add('desktop:hidden');
      this.#menuDrawerHiddenWidth = null;
      this.refs.headerMenu.classList.remove('hidden');
    }
  }

  #setStickyState(stickyState, scrollDirection = 'none') {
    const hasStickyStateChanged = this.dataset.stickyState !== stickyState;
    const hasScrollDirectionChanged = this.dataset.scrollDirection !== scrollDirection;

    if (hasStickyStateChanged) this.dataset.stickyState = stickyState;
    if (hasScrollDirectionChanged) this.dataset.scrollDirection = scrollDirection;

    if (hasStickyStateChanged || hasScrollDirectionChanged) {
      changeMetaThemeColor(this.refs.headerRowTop);
    }
  }

  #setScrollUpVisibleState(scrollTop, scrollDirection = 'up') {
    const nearTop = scrollTop <= this.#topVisibilityThreshold;
    this.#setStickyState(nearTop ? 'inactive' : 'active', nearTop ? 'none' : scrollDirection);
  }

  #syncAlwaysStickyState(scrollTop, scrollDelta) {
    if (scrollTop <= this.#topVisibilityThreshold || Math.abs(scrollDelta) < this.#scrollThreshold) {
      this.dataset.scrollDirection = 'none';
      return;
    }

    this.dataset.scrollDirection = scrollDelta < 0 ? 'up' : 'down';
  }

  #syncScrollUpStickyState(scrollTop, scrollDelta) {
    if (scrollTop <= this.#topVisibilityThreshold) {
      this.#trackedDirection = 'none';
      this.#directionTravel = 0;
      this.#setScrollUpVisibleState(scrollTop, 'none');
      return;
    }

    if (Math.abs(scrollDelta) < this.#scrollThreshold) return;

    const nextDirection = scrollDelta > 0 ? 'down' : 'up';
    if (nextDirection !== this.#trackedDirection) {
      this.#trackedDirection = nextDirection;
      this.#directionTravel = Math.abs(scrollDelta);
    } else {
      this.#directionTravel += Math.abs(scrollDelta);
    }

    if (this.#directionTravel < this.#directionSwitchThreshold) return;

    if (nextDirection === 'down') {
      this.#setStickyState('idle', 'down');
    } else {
      this.#setScrollUpVisibleState(scrollTop, 'up');
    }

    this.#directionTravel = 0;
  }

  #syncStickyState = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!stickyMode) return;

    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    const scrollDelta = scrollTop - this.#lastScrollTop;

    if (stickyMode === 'always') {
      this.#syncAlwaysStickyState(scrollTop, scrollDelta);
    } else if (stickyMode === 'scroll-up') {
      this.#syncScrollUpStickyState(scrollTop, scrollDelta);
    }

    this.#lastScrollTop = scrollTop;
  };

  #handleWindowScroll = () => {
    if (this.#scrollRaf) return;

    this.#scrollRaf = window.requestAnimationFrame(() => {
      this.#scrollRaf = 0;
      this.#syncStickyState();
    });
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#lastScrollTop = document.scrollingElement?.scrollTop ?? 0;

      if (stickyMode === 'always') {
        this.#observeStickyPosition(true);
      } else if (stickyMode === 'scroll-up') {
        this.#setScrollUpVisibleState(this.#lastScrollTop, 'none');
      }

      document.addEventListener('scroll', this.#handleWindowScroll, { passive: true });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    document.removeEventListener('scroll', this.#handleWindowScroll);
    if (this.#scrollRaf) {
      window.cancelAnimationFrame(this.#scrollRaf);
      this.#scrollRaf = 0;
    }
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');

  // Note: Initial header heights are set via inline script in theme.liquid
  // This ResizeObserver handles dynamic updates after page load

  // Update header group height on resize of any child
  if (headerGroup) {
    const resizeObserver = new ResizeObserver((entries) => {
      const headerGroupHeight = entries.reduce((totalHeight, entry) => {
        if (
          entry.target !== header ||
          (header.hasAttribute('transparent') && header.parentElement?.nextElementSibling)
        ) {
          return totalHeight + (entry.borderBoxSize[0]?.blockSize ?? 0);
        }
        return totalHeight;
      }, 0);
      // The initial height is calculated using the .offsetHeight property, which returns an integer.
      // We round to the nearest integer to avoid unnecessaary reflows.
      const roundedHeaderGroupHeight = Math.round(headerGroupHeight);
      document.body.style.setProperty('--header-group-height', `${roundedHeaderGroupHeight}px`);
    });

    if (header instanceof HTMLElement) {
      resizeObserver.observe(header);
    }

    // Observe all children of the header group
    const children = headerGroup.children;
    for (let i = 0; i < children.length; i++) {
      const element = children[i];
      if (element instanceof HTMLElement) {
        resizeObserver.observe(element);
      }
    }

    // Also observe the header group itself for child changes
    const mutationObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          // Re-observe all children when the list changes
          const children = headerGroup.children;
          for (let i = 0; i < children.length; i++) {
            const element = children[i];
            if (element instanceof HTMLElement) {
              resizeObserver.observe(element);
            }
          }
        }
      }
    });

    mutationObserver.observe(headerGroup, { childList: true });
  }
});
